import { load as loadRecaptcha } from 'recaptcha-v3';

export default class FormNewsletter extends HTMLElement {
    constructor(m) {
        super(m);

        // Binding event handlers to the class instance
        this.onFormSubmit = this.onFormSubmit.bind(this);

        // Select key UI elements from the DOM
        this.$form = this.querySelector('[data-form]');
        this.$formInner = this.querySelector('[data-form-inner]');
        this.$feedback = this.querySelector('[data-form-feedback]');
        this.$feedbackMessage = this.querySelector('[data-form-feedback-message]');

        // Component state data
        this.isLoading = false; // Prevents multiple form submissions
        this.formErrors = false; // Tracks if there are form validation errors
        this.successFeedback = this.$form.dataset['successFeedback'];
        this.errorFeedback = this.$form.dataset['errorFeedback'];
        this.recaptchaPubkey = this.$form.dataset['recaptchaPubkey'];
    }

    // =============================================================================
    // Lifecycle methods
    // =============================================================================
    connectedCallback() {
        // Bind event listeners and initialize any UI state updates
        this.bindEvents();
    }

    disconnectedCallback() {
        // Unbind event listeners when the component is disconnected
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        // Bind form submission event
        this.$form.addEventListener('submit', this.onFormSubmit);
    }

    unbindEvents() {
        // Remove previously bound event listeners
        this.$form.removeEventListener('submit', this.onFormSubmit);
    }

    // =============================================================================
    // Form submission handling
    // =============================================================================
    async onFormSubmit(e) {
        e.preventDefault();
        e.stopPropagation();

        if (this.isLoading) return;

        // Collect and remove duplicate form data fields
        let formData = new FormData(this.$form);
        formData = this.removeDuplicateFormData(formData);

        // Be ready to fetch
        this.formErrors = false;
        this.feedback(false);
        this.setLoading();

        await new Promise((resolve) => setTimeout(resolve, 3000));

        // Add recaptcha token if applicable
        if (this.recaptchaPubkey) {
            const recaptcha = await loadRecaptcha(this.recaptchaPubkey, { autoHideBadge: true });
            const token = await recaptcha.execute();
            formData.append('g-recaptcha-response', token);
        }

        // Submit the form data via fetch
        fetch(this.$form.getAttribute('action'), {
            method: this.$form.method,
            body: formData
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.success) {
                    throw (this.formErrors = response.errors);
                }
            })
            .catch((e) => {
                e && console.error(e);

                // Provide default error feedback if no errors were returned
                if (!this.formErrors) {
                    this.formErrors = [
                        ...(e && e.data && typeof e.data === 'string' ? [{ message: e.data }] : []),
                        { message: this.errorFeedback }
                    ];

                    this.feedback(
                        this.formErrors.map((error) => error.message).join('<br>'),
                        'error'
                    );
                }
            })
            .finally((response) => {
                this.setLoading(false);

                // Display success feedback if there were no errors
                if (!this.formErrors) {
                    this.feedback(response && response.data ? response.data : this.successFeedback);
                    this.reset();
                }
            });
    }

    // =============================================================================
    // Methods
    // =============================================================================

    // Removes duplicate keys from the FormData object
    removeDuplicateFormData(formData) {
        const uniqueData = new Map();

        for (let [key, value] of formData.entries()) {
            uniqueData.set(key, value);
        }

        const deduplicatedFormData = new FormData();
        for (let [key, value] of uniqueData) {
            deduplicatedFormData.append(key, value);
        }

        return deduplicatedFormData;
    }

    // Toggles the loading state of the form
    setLoading(value = true) {
        this.isLoading = value;
        this.classList.toggle('is-loading', this.isLoading);
    }

    // Displays feedback messages (success or error)
    feedback(message, type = 'success') {
        if (!message) {
            this.$feedback.classList.add('u-hidden');
            this.$feedback.removeAttribute('aria-live');
            return;
        }

        // Update feedback message and make it visible
        this.$feedback.classList.remove('u-hidden');
        this.$feedback.setAttribute('aria-live', 'polite');
        this.$feedbackMessage.innerHTML = message;

        // Remove previous feedback type classes and apply the new one
        for (let modifier of ['error', 'success']) {
            this.$feedback.classList.remove(`-${modifier}`);
        }
        this.$feedback.classList.add(`-${type}`);

        // Focus to the feedback message for accessibility
        this.$feedbackMessage.focus();
    }

    // Resets the form and all related UI states
    reset() {
        this.$form.reset();
    }
}
